import { ComponentType } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { ParkingLocation } from '../types/parkingLocation';

export type StageId =
	| 'action'
	| 'carPhoto'
	| 'ticket'
	| 'confirm'
	| 'towInfo';

export interface StageDefinition {
	id: StageId;
	label: string;
	shortLabel: string;
	icon: ComponentType;
	visible: (lot: ParkingLocation | null) => boolean;
	branch?: 'postSubmit';
}

// All four stage gates use positive naming: true = stage is in the pipeline.
// `lot == null` returns the defaults (action + ticket + confirm visible) so
// the FlowPreview renders sensibly on a fresh form.
export const STAGES: StageDefinition[] = [
	{
		id: 'action',
		label: 'Action',
		shortLabel: 'Action',
		icon: InfoOutlinedIcon,
		visible: (lot) => lot?.showActionStage ?? true,
	},
	{
		id: 'carPhoto',
		label: 'Vehicle photo',
		shortLabel: 'Photo',
		icon: CameraAltOutlinedIcon,
		visible: (lot) => !!lot?.showCarPhotoStage,
	},
	{
		id: 'ticket',
		label: 'Enter details',
		shortLabel: 'Details',
		icon: ReceiptLongOutlinedIcon,
		visible: () => true,
	},
	{
		id: 'confirm',
		label: 'Confirm',
		shortLabel: 'Confirm',
		icon: FactCheckOutlinedIcon,
		visible: (lot) => lot?.showConfirmStage ?? true,
	},
	{
		id: 'towInfo',
		label: 'Tow instructions',
		shortLabel: 'Tow',
		icon: LocalShippingOutlinedIcon,
		visible: () => true,
		branch: 'postSubmit',
	},
];

export const getStage = (id: StageId): StageDefinition => {
	const stage = STAGES.find((s) => s.id === id);
	if (!stage) throw new Error(`Unknown stage: ${id}`);
	return stage;
};

// The linear pipeline for a given lot — branch stages (towInfo) are excluded.
export const buildPipeline = (
	lot: ParkingLocation | null,
): StageDefinition[] => STAGES.filter((s) => !s.branch && s.visible(lot));

export const TOW_BRANCH = getStage('towInfo');
